import type { Descendant } from 'slate'

import cx from 'classnames'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { MdAttachFile, MdOutlineCloudUpload, MdOutlineDelete, MdSend } from 'react-icons/md'
import { animated, useSpring } from 'react-spring'
import { Editor, Node, Transforms, createEditor } from 'slate'
import { withHistory } from 'slate-history'
import { ReactEditor, Editable, Slate, withReact } from 'slate-react'

import { useDebounce } from '../../../hooks'
import { SUPPORTED_FILETYPE } from '../../chat/components/active-chat'
import { PDFIcon } from '../../chat/components/icons/PDFIcon'
import { nonNullablePredicate } from '../common'

function getDefaultEditorValue (): Descendant[] {
  return [
    {
      // @ts-expect-error // It's fine
      type: 'paragraph',
      children: [
        { text: '' }
      ]
    }
  ]
}

function round (value: number, precision = 2): number {
  let k = Math.pow(10, precision)
  return Math.round(value * k) / k
}

function humanReadableBytes (bytes: number): string {
  let i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${round(bytes / Math.pow(1024, i))} ${[ 'B', 'kB', 'MB', 'GB', 'TB' ][i] ?? 'N/A'}`
}

interface SubmitHelpers {
  clearValue: () => void
  revertValue: () => void
}

interface Message {
  text?: string
  files: File[]
}

export interface EditorRef {
  setValue: (value: Descendant[]) => void
  focus: () => void
}

export interface MessageEditorProps {
  chatId: string
  isDisabled?: boolean
  onChange?: (value: Descendant[]) => void
  onSubmit: (message: Message, helpers: SubmitHelpers) => void
}

export let MessageEditor = forwardRef<EditorRef, MessageEditorProps>(function MessageEditor ({ chatId, isDisabled = false, onChange, onSubmit }, ref) {
  let fileInputRef = useRef<HTMLInputElement>(null)
  let [ file, setFile ] = useState<File | null>(null)
  let debouncedFile = useDebounce(file, file ? 0 : 2000)
  let [ isDraggingOver, setIsDraggingOver ] = useState(false)

  let [ editor ] = useState(() => withReact(withHistory(createEditor())))
  let [ value, setValue ] = useState(getDefaultEditorValue())

  let valueRef = useRef(value)
  let fileRef = useRef(file)

  let clearValue = useCallback(() => {
    let newValue = getDefaultEditorValue()
    setValue(newValue)
    setFile(null)

    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, [])
      }
    })

    Transforms.removeNodes(editor, {
      at: [ 0 ]
    })

    Transforms.insertNodes(
      editor,
      newValue
    )
  }, [ editor ])

  let revertValue = useCallback(() => {
    let oldValue = valueRef.current
    let oldFile = fileRef.current

    setValue(oldValue)
    setFile(oldFile)

    valueRef.current = getDefaultEditorValue()
    fileRef.current = null

    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, [])
      }
    })

    Transforms.removeNodes(editor, {
      at: [ 0 ]
    })

    Transforms.insertNodes(
      editor,
      oldValue
    )
  }, [ editor ])

  let submit = useCallback(() => {
    let text = value.map(node => Node.string(node)).join('\n')

    if (
      !file && (typeof text !== 'string' || !text.length)
    ) {
      return
    }

    valueRef.current = value
    fileRef.current = file

    clearValue()

    let message: Message = {
      text: text || undefined,
      files: [ file ].filter(nonNullablePredicate)
    }

    onSubmit(message, {
      clearValue,
      revertValue
    })
  }, [ clearValue, onSubmit, revertValue, value, file ])

  let attachmentsPanelStyles = useSpring({
    opacity: file
      ? 1
      : 0.75,

    transform: file
      ? 'translateY(-100%)'
      : 'translateY(0%)'
  })

  let dropzoneStyles = useSpring({
    opacity: isDraggingOver
      ? 1
      : 0
  })

  let handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    let file = event.dataTransfer.files?.[0]

    if (!file) {
      return
    }

    let accepts = fileInputRef.current?.accept?.split(', ') ?? []

    if (!accepts.includes(file.type)) {
      return
    }

    setIsDraggingOver(false)

    if (!SUPPORTED_FILETYPE.includes(file?.type)) {
      toast.error(`${file?.type || 'Undefined file type'} is not supported.\nSupported attachment types: png, jpeg, pdf, gif.`, {
        position: 'bottom-center',
        duration: 4000
      })
    }

    if (file?.size > 10485760) {
      toast.error('Attachment larger than 10MB.', {
        position: 'bottom-center',
        duration: 4000
      })

      return
    }

    setFile(file)
  }, [])

  let handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    setIsDraggingOver(true)
  }, [])

  let handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    setIsDraggingOver(true)
  }, [])

  let handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    setIsDraggingOver(false)
  }, [])

  let focus = useCallback(() => {
    ReactEditor.focus(editor)
  }, [ editor ])

  let handleExternalSetValue = useCallback((value: Descendant[]) => {
    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, [])
      }
    })

    Transforms.removeNodes(editor, {
      at: [ 0 ]
    })

    Transforms.insertNodes(editor, value)
    Transforms.select(editor, Editor.end(editor, []))

    setValue(value)
  }, [ editor ])

  useEffect(() => {
    // setTimeout(() => {
    //   focus()
    // }, 1000)

    if (!ref) {
      return
    }

    if (typeof ref === 'function') {
      ref({
        focus,
        setValue: handleExternalSetValue
      })
      return
    }

    ref.current = {
      focus,
      setValue: handleExternalSetValue
    }
  }, [ ref, focus, handleExternalSetValue ])

  useEffect(() => {
    clearValue()
  }, [ chatId, clearValue ])

  let handleChange = useCallback((value: Descendant[]) => {
    setValue(value)
    onChange?.(value)
  }, [ onChange ])

  return (
    <div
      className='relative z-10 bg-white max-w-full'
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      <div className='flex flex-grow items-end z-20 relative bg-white max-w-full w-full'>
        <input
          type='file'
          accept='image/jpeg, image/png, application/pdf, image/gif'
          ref={fileInputRef}
          className='hidden'
          onChange={event => {
            const file = event.target.files?.[0]

            if (!file) return

            if (!SUPPORTED_FILETYPE.includes(file.type)) {
              toast.error(`${file?.type || 'Undefined file type'} is not supported.\nSupported attachment types: png, jpeg, pdf, gif.`, {
                position: 'bottom-center',
                duration: 4000
              })
            }

            if ((file.size ?? 0) > 10485760) {
              toast.error('Attachment larger than 10MB.', {
                position: 'bottom-center',
                duration: 4000
              })
              return
            }
            setFile(file ?? null)
          }}
        />

        <button
          type='button'
          className='flex justify-center items-center transition-colors text-gray-600 pl-3 px-2 py-4'
          title='Attach file'
          onClick={() => {
            fileInputRef.current?.click()
          }}
        >
          <MdAttachFile
            size={20}
            className='text-gray-600'
          />
        </button>

        <div
          className='flex flex-col h-auto min-h-full flex-grow max-w-[calc(100%_-_80px)] w-full'
        >
          <Slate
            editor={editor}
            initialValue={value}
            onChange={handleChange}
          >
            <Editable
              className='min-h-[max-content] max-h-48 overflow-y-auto font-roboto text-base text-gray-800 pb-[17px] pt-[18px] outline-none leading-tight whitespace-break-spaces break-words'
              placeholder='Type a message...'
              disabled={isDisabled}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  if (event.shiftKey) {
                    return
                  }

                  event.preventDefault()
                  submit()
                }
              }}
            />
          </Slate>
        </div>

        <button
          type='button'
          className='flex justify-center items-center transition-colors text-gray-600 px-2 pr-3 py-4'
          title='Send'
          onClick={() => {
            submit()
          }}
        >
          <MdSend
            size={20}
            className='text-gray-600'
          />
        </button>
      </div>

      <animated.div
        className='w-full min-h-full absolute top-0 left-0 z-10 rounded-t-lg bg-white p-3 flex flex-row gap-3 justify-start items-start border-b border-t border-gray-200 shadow-2xl'
        style={attachmentsPanelStyles}
      >
        <div className='w-[3.25rem] h-[3.25rem] rounded flex-shrink-0 flex justify-center items-center overflow-hidden'>
          {debouncedFile?.type.endsWith('pdf') && (
            <PDFIcon
              style={{
                objectFit: 'cover'
              }}
            />
          )}

          {debouncedFile?.type.startsWith('image/') && (
            // eslint-disable-next-line jsx-a11y/img-redundant-alt
            <img
              src={URL.createObjectURL(debouncedFile)}
              alt='Attached image'
              className='h-full w-full object-cover'
            />
          )}
        </div>

        <div className='flex flex-col gap-1 flex-grow'>
          <span
            className='font-roboto text-sm font-medium leading-tight line-clamp-2 text-gray-800'
            title='Lorem ipsum dolor, sit amet consectetur adipisicing elit. Reiciendis alias architecto fuga iure obcaecati optio vel, itaque libero soluta placeat aut in, amet illo quo hic, ipsum molestiae! Distinctio, sequi.pdf'
          >
            {debouncedFile?.name ?? 'Untitled'}
          </span>

          <span className='font-roboto text-xs font-normal line-clamp-1 text-gray-600'>
            {humanReadableBytes(debouncedFile?.size ?? 0)}
          </span>
        </div>

        <div className='flex flex-col self-center justify-center items-center'>
          <button
            type='button'
            className='flex justify-center items-center transition-colors text-gray-500 p-3 -m-3 rounded-full hover:text-gray-600 flex-shrink-0'
            title='Remove'
            onClick={() => {
              setFile(null)
            }}
          >
            <MdOutlineDelete
              size={18}
              className='text-inherit'
            />
          </button>
        </div>
      </animated.div>

      <animated.div
        className='absolute top-0 left-0 w-full h-full bg-gray-100 bg-opacity-50 backdrop-blur-sm flex justify-center items-center z-30 pointer-events-none font-roboto text-sm font-normal text-gray-700 p-2 gap-3 shadow-inner'
        data-cy='drop-target'
        style={dropzoneStyles}
      >
        <MdOutlineCloudUpload
          size={20}
          className={cx('text-gray-700 flex-shrink-0', {
            'animate-pulse': isDraggingOver
          })}
        />

        <div>
          Drop file here
        </div>
      </animated.div>
    </div>
  )
})

MessageEditor.displayName = 'MessageEditor'
